import { IFeature } from '../../components/product-filters-menu/interfaces/feature.interface';
import { FeatureFilter } from '../../components/product-filters-menu/models/feature-filter.model';
import { ProductFeatureDetailsDto } from '../models/product-feature-details-dto.model';
import { SchemaValueFields } from '../models/schema-value-fielts.model';

const matchesEnumFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    const allowedValues = filter.values;
    if (!allowedValues || allowedValues.length === 0) {
        return true;
    }
    if (value.enumValue) {
        return allowedValues.includes(value.enumValue);
    }
    if (value.enumValues && value.enumValues.length > 0) {
        return value.enumValues.some((enumValue) => allowedValues.includes(enumValue));
    }
    if (value.textValue) {
        return allowedValues.includes(value.textValue);
    }
    if (typeof value.numericValue === 'number') {
        return allowedValues.includes(value.numericValue.toString());
    }
    return false;
};

const matchesBooleanFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    if (typeof filter.booleanValue !== 'boolean') {
        return true;
    }
    return value.booleanValue === filter.booleanValue;
};

const matchesRangeFilter = (value: SchemaValueFields, filter: FeatureFilter): boolean => {
    const from = parseFloat(filter.rangeFrom ?? '');
    const to = parseFloat(filter.rangeTo ?? '');
    if (Number.isNaN(from) || Number.isNaN(to)) {
        return true;
    }
    if (typeof value.numericRangeFrom === 'number' && typeof value.numericRangeTo === 'number') {
        return value.numericRangeFrom <= to && value.numericRangeTo >= from;
    }
    if (typeof value.numericValue === 'number') {
        return value.numericValue >= from && value.numericValue <= to;
    }
    if (value.textValue) {
        const parsed = parseFloat(value.textValue.replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            return parsed >= from && parsed <= to;
        }
    }
    return false;
};

const matchesSingleFilter = (
    productFeatureDetails: ProductFeatureDetailsDto[],
    filter: FeatureFilter,
    availableFeatures: IFeature[]
): boolean => {
    const feature = availableFeatures.find((f) => f.id === filter.featureId);
    if (!feature) {
        return true;
    }
    const detail = productFeatureDetails.find((d) => d.featureCode === feature.code);
    if (!detail || !detail.value) {
        return false;
    }

    switch (filter.valueType) {
        case 'enum':
            return matchesEnumFilter(detail.value, filter);
        case 'boolean':
            return matchesBooleanFilter(detail.value, filter);
        case 'range':
            return matchesRangeFilter(detail.value, filter);
        default:
            return true;
    }
};

export const productMatchesFeatureFilters = (
    productFeatureDetails: ProductFeatureDetailsDto[] | undefined,
    featuresFilters: FeatureFilter[],
    availableFeatures: IFeature[]
): boolean => {
    if (!featuresFilters || featuresFilters.length === 0) {
        return true;
    }
    if (!productFeatureDetails || productFeatureDetails.length === 0) {
        return false;
    }
    return featuresFilters.every((filter) => matchesSingleFilter(productFeatureDetails, filter, availableFeatures));
};
